import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendRequest } from '@infrastructure/api/api';
import { getApiScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import type { DashboardResponse } from '../types/dashboard';

export function useDashboard(scope: string, scopeId: number) {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchDashboard = useCallback(async () => {
    if (!scope || !scopeId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setAccessDenied(false);

    try {
      const apiScope = getApiScopeFromValue(scope);
      const response = await sendRequest<DashboardResponse, unknown>(
        'GET',
        `${apiScope}/${scopeId}/dashboard`
      );

      if (response.status === 403) {
        setAccessDenied(true);
        setData(null);
        return;
      }

      const raw = response?.getData?.() ?? response?.data ?? null;
      const payload = raw as DashboardResponse | null;
      if (payload?.meta) {
        setData(payload);
      } else {
        setError('Failed to load dashboard data.');
        setData(null);
      }
    } catch {
      setError('Failed to load dashboard. Please try again.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (accessDenied) {
      navigate('/entities', { replace: true, state: { accessDenied: true } });
    }
  }, [accessDenied, navigate]);

  return { data, loading, error, refresh: fetchDashboard, accessDenied };
}
